import { useNavigate } from '@tanstack/react-router';
import { memo, useCallback } from 'react';

import blurredBackground from '@/assets/bg-blurred.webp';
import { merge } from '@/components';
import { Button, Flex, FlexProps, Heading, Link, Text } from '@/components/core';
import { StarsIcon } from '@/components/icons';
import { CradlLogo } from '@/components/logos';
import { DEFAULT_TRIAL_DAYS } from '@/constants';
import { checkOrganizationTrialStatus } from '@/hooks';
import { toOrganizationId, useGetOrganization, useListOrganizations } from '@/hooks/api';

export type UpgradeProps = FlexProps;

const IUpgrade = ({ className, ...rest }: UpgradeProps) => {
  const navigate = useNavigate();

  const { data: organization } = useGetOrganization(toOrganizationId('me'));
  const { trialDays } = checkOrganizationTrialStatus(organization);

  const { data: organizations } = useListOrganizations();
  const hasOtherOrganizations = organizations?.data?.length;

  const onTalkToSales = useCallback(() => {
    window.location.href = 'https://cal.com/cradlai/30min';
  }, []);

  return (
    <Flex
      col
      className="gap-lg relative h-screen w-full items-center justify-center overflow-hidden bg-cover bg-center"
      style={{ backgroundImage: `url(${blurredBackground})` }}
    >
      <Flex col className={merge('p-lg gap-lg w-[35rem] items-center rounded-lg bg-white', className)} {...rest}>
        <Flex col className="gap-md px-lg items-center text-center">
          <Flex className="relative w-full justify-center">
            <CradlLogo size="xl" className="size-12" />
          </Flex>
          <Flex col className="gap-sm">
            <Heading size="xl">Your free trial has ended</Heading>
            <Text>
              Your {trialDays || DEFAULT_TRIAL_DAYS}-day free trial for the workspace{' '}
              <strong>{organization?.name}</strong> has ended. To keep using Cradl AI, please subscribe to a plan that
              works for you.
            </Text>
          </Flex>
        </Flex>

        <Flex col className="px-md gap-md w-full items-center">
          <Button variant="primary" onPress={() => navigate({ to: '/settings/plans' })} className="px-lg">
            <StarsIcon />
            <Text>See plans</Text>
          </Button>

          <Flex>
            <Text>
              Questions? <Link onPress={onTalkToSales}>Book a call</Link>.
            </Text>
          </Flex>

          {hasOtherOrganizations && (
            <Text className="mt-md italic">
              Logged into the wrong workspace?{' '}
              <Link onPress={() => navigate({ to: '/organizations' })}>Switch workspace.</Link>
            </Text>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};

export const Upgrade = memo(IUpgrade);
